import React from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { t } from '../locales/translations';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { scrollToPricing } from '../lib/stripe';
import { LOGO_URL } from '../assets/index';

const ExtensionPreview = () => {
  const { language } = useLanguage();
  const text = t[language].extensionPreview;

  return (
    <section id="features" className="py-16 sm:py-24 bg-[#0f0f13] relative overflow-hidden">
      <div className="absolute top-0 right-1/4 w-[500px] h-[500px] bg-orange-500/5 rounded-full blur-[100px] pointer-events-none" />

      <div className="container mx-auto px-4 sm:px-6">
        <div className="text-center max-w-3xl mx-auto mb-12 sm:mb-16">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4"
          >
            {text.title}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-base sm:text-xl text-gray-400"
          >
            {text.subtitle}
          </motion.p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="relative w-full max-w-sm mx-auto"
          >
            <div className="absolute inset-0 bg-gradient-to-tr from-orange-500 to-purple-500 rounded-3xl blur-2xl opacity-20" />

            <div className="relative bg-[#0d0f14] rounded-[2rem] border-[6px] border-[#1a1a24] shadow-2xl overflow-hidden flex flex-col min-h-[550px]">
              <div className="bg-[#15171e] px-5 py-3 flex items-center justify-between border-b border-white/5">
                <div className="flex gap-2">
                  <div className="w-3 h-3 rounded-full bg-[#ff5f56]" />
                  <div className="w-3 h-3 rounded-full bg-[#ffbd2e]" />
                  <div className="w-3 h-3 rounded-full bg-[#27c93f]" />
                </div>
                <span className="text-gray-500 font-bold text-[10px] tracking-widest uppercase">LOVAGO V2.5</span>
              </div>

              <div className="p-6 flex-grow flex flex-col">
                <div className="flex-grow flex flex-col">
                  <div className="flex items-center justify-between mb-8">
                    <img src={LOGO_URL} alt="LovaGO" className="w-10 h-10 object-contain" />
                    <div className="flex items-center gap-3">
                      <div className="bg-purple-500/10 border border-purple-500/30 text-purple-400 px-3 py-1.5 rounded-lg text-xs font-bold tracking-wider">
                        {text.proActive}
                      </div>
                      <div className="w-9 h-9 rounded-full border border-white/10 flex items-center justify-center bg-[#15171e]">
                        <SafeIcon icon={FiIcons.FiShield} className="text-gray-400 text-sm" />
                      </div>
                    </div>
                  </div>

                  <div className="bg-gradient-to-br from-[#1e1b38] to-[#161224] rounded-2xl p-6 text-center border border-purple-500/20 shadow-2xl mb-8 relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-32 h-32 bg-purple-500/10 blur-3xl rounded-full pointer-events-none" />
                    <p className="text-gray-400 text-[10px] font-black tracking-[0.15em] mb-3 uppercase">{text.subDetected}</p>
                    <h3 className="text-white text-3xl font-black italic tracking-wide mb-4 drop-shadow-lg">{text.levelPro}</h3>
                    <div className="flex items-center justify-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-purple-500 shadow-[0_0_8px_#a855f7]" />
                      <span className="text-purple-400 font-bold text-sm">{text.monthlyFee}</span>
                    </div>
                  </div>

                  <div className="space-y-5 mb-8 px-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <SafeIcon icon={FiIcons.FiZap} className="text-orange-400 text-lg" />
                        <span className="text-white font-bold text-sm">{text.aiTokens}</span>
                      </div>
                      <span className="text-white font-black tracking-wide text-sm">{text.unlimited}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <SafeIcon icon={FiIcons.FiCloud} className="text-green-400 text-lg" />
                        <span className="text-white font-bold text-sm">{text.deploys}</span>
                      </div>
                      <span className="text-white font-black tracking-wide text-sm">{text.unlimited}</span>
                    </div>
                  </div>

                  <button
                    onClick={scrollToPricing}
                    className="w-full mt-auto py-4 rounded-xl bg-[#ea580c] hover:bg-orange-700 text-white font-bold text-sm relative overflow-hidden transition-colors flex items-center justify-center gap-2 shadow-[0_4px_20px_rgba(234,88,12,0.4)]"
                  >
                    <SafeIcon icon={FiIcons.FiPower} className="text-lg" />
                    {text.activateBtn}
                  </button>
                </div>
              </div>
            </div>
          </motion.div>

          <div className="space-y-4">
            {text.features.map((feat, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="flex items-center gap-4 bg-[#1a1a24] p-4 rounded-xl border border-white/5"
              >
                <div className="w-10 h-10 rounded-full bg-orange-500/10 flex items-center justify-center shrink-0">
                  <SafeIcon icon={FiIcons.FiCheckCircle} className="text-orange-400 text-xl" />
                </div>
                <span className="text-white font-medium">{feat}</span>
              </motion.div>
            ))}

            <motion.button
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              onClick={scrollToPricing}
              className="mt-6 w-full py-4 bg-[#ea580c] hover:bg-orange-700 shadow-[0_4px_20px_rgba(234,88,12,0.4)] rounded-xl text-white font-bold transition-colors flex items-center justify-center gap-2"
            >
              {text.activateBtn} <SafeIcon icon={FiIcons.FiArrowRight} />
            </motion.button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ExtensionPreview;